import { useState } from "react";
import { toast } from "sonner";
import {
  BadgePercent,
  Loader2,
  Mail,
  MoreVertical,
  RefreshCw,
  RotateCcw,
  Undo2,
  Wallet,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useMarkBillingPaid,
  useRecalculateBilling,
  useSendInvoiceEmail,
  useWaiveBilling,
} from "@/hooks/use-billing";
import { describeCallableError } from "@/lib/callables";
import { formatCurrency, formatMonthYear } from "@/lib/format";
import type { SellerBilling } from "@/types";

type Confirming = "pay" | "reopen" | "waive" | "unwaive";

const TITLES: Record<Confirming, string> = {
  pay: "Dar baixa nesta fatura?",
  reopen: "Reabrir esta fatura?",
  waive: "Ignorar esta fatura?",
  unwaive: "Voltar a cobrar esta fatura?",
};

const PLACEHOLDERS: Record<Confirming, string> = {
  pay: "Transferência recebida em 12/03, acordo, ...",
  reopen: "Baixa indevida, cobrança corrigida, ...",
  waive: "Cortesia de lançamento, cobrança indevida, ...",
  unwaive: "Ignorada por engano, acordo desfeito, ...",
};

/** "julho de 2026 e agosto de 2026", para dizer quais dívidas vão junto. */
function debtMonths(billing: SellerBilling): string {
  const labels = (billing.expiredDebtItems ?? [])
    .filter((item) => item.month && item.year)
    .map((item) => formatMonthYear(item.month as number, item.year as number).toLowerCase());
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
}

/**
 * As ações de uma fatura, num menu só.
 *
 * As que mexem em dinheiro (baixa, estorno, ignorar e voltar a cobrar) pedem
 * confirmação e aceitam um motivo, que fica gravado no documento. Sem ele,
 * ninguém sabe depois se aquela fatura foi quitada por acordo, por
 * transferência ou por engano, nem por que deixou de ser cobrada.
 */
export function BillingActions({ billing }: { billing: SellerBilling }) {
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [reason, setReason] = useState("");

  const markPaid = useMarkBillingPaid();
  const waive = useWaiveBilling();
  const recalculate = useRecalculateBilling();
  const sendEmail = useSendInvoiceEmail();

  const saving = markPaid.isPending || waive.isPending;
  const busy = saving || recalculate.isPending || sendEmail.isPending;
  const absorbed = Boolean(billing.accumulatedIntoBillingId);
  const amount = billing.totalAmount ?? billing.platformFee ?? 0;
  const debt = billing.expiredDebt ?? 0;
  const period =
    billing.month && billing.year ? formatMonthYear(billing.month, billing.year) : "";
  const who = billing.sellerName || "Este estabelecimento";
  // Ignorada com dívida acumulada: a comissão já saiu, o que resta ignorar são
  // as dívidas.
  const waivingDebtsOnly = billing.waived === true && !billing.paid;
  const pendingCharge = billing.pendingPayment?.status === "pending";

  function close() {
    setConfirming(null);
    setReason("");
  }

  async function confirm() {
    if (!confirming) return;
    const note = reason.trim() || undefined;
    try {
      if (confirming === "pay" || confirming === "reopen") {
        const paying = confirming === "pay";
        await markPaid.mutateAsync({
          billingId: billing.id,
          paid: paying,
          reason: note,
          sellerId: billing.sellerId,
        });
        toast.success(
          paying
            ? "Fatura marcada como paga. As absorvidas por ela são quitadas em seguida."
            : "Fatura reaberta.",
        );
      } else {
        const result = await waive.mutateAsync({
          billingId: billing.id,
          waived: confirming === "waive",
          reason: note,
          sellerId: billing.sellerId,
        });
        toast.success(
          result.message ??
            (confirming === "waive" ? "Fatura ignorada." : "A fatura voltou a ser cobrada."),
        );
      }
      close();
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  async function recalc() {
    if (!billing.sellerId) return;
    try {
      const result = await recalculate.mutateAsync({ sellerId: billing.sellerId });
      toast.success(
        `Recalculado: ${result.monthsProcessed} meses, à taxa de ${result.platformFeePercentage}%.`,
      );
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  async function email() {
    if (!billing.sellerId || !billing.month || !billing.year) return;
    try {
      const result = await sendEmail.mutateAsync({
        sellerId: billing.sellerId,
        month: billing.month,
        year: billing.year,
      });
      toast.success(`Fatura enviada para ${result.sentTo}.`);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  function description(mode: Confirming) {
    switch (mode) {
      case "pay":
        return (
          <>
            {who}, {period}, no valor de {formatCurrency(amount)}. As faturas absorvidas por ela
            são quitadas em seguida, e o estabelecimento é desbloqueado.
          </>
        );
      case "reopen":
        return (
          <>
            {who}, {period}. A fatura volta a contar como dívida, e o estabelecimento pode ser
            bloqueado no próximo fechamento se ela estiver vencida.
          </>
        );
      case "waive":
        if (waivingDebtsOnly) {
          return (
            <>
              {who}, {period}. A comissão deste mês já está ignorada; o que a fatura cobra são as
              dívidas de meses anteriores acumuladas nela ({formatCurrency(debt)}
              {debtMonths(billing) ? `, de ${debtMonths(billing)}` : ""}). Elas são ignoradas
              também, e a fatura passa a valer como paga.
            </>
          );
        }
        return (
          <>
            {who}, {period}. A comissão do Zé Padoca de{" "}
            {formatCurrency(billing.platformFee ?? 0)} deixa de ser cobrada
            {debt > 0
              ? `, e as dívidas acumuladas nela (${formatCurrency(debt)}${debtMonths(billing) ? `, de ${debtMonths(billing)}` : ""}) são ignoradas junto`
              : ""}
            . A fatura passa a valer como paga, e o estabelecimento é desbloqueado se não restar
            dívida vencida.
            {absorbed ? " Ela está acumulada em outra fatura, que deixa de cobrá-la." : ""}
            {pendingCharge ? " O Pix ou boleto já gerado para ela é cancelado." : ""}
          </>
        );
      case "unwaive":
        return (
          <>
            {who}, {period}. A comissão de {formatCurrency(billing.waivedPlatformFee ?? 0)} volta a
            ser cobrada, e as faturas ignoradas junto com esta voltam também. As dívidas antigas
            voltam a se acumular no cálculo diário. Se o estabelecimento é parceiro de teste neste
            mês, tire-o da parceria antes.
          </>
        );
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Ações da fatura" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" /> : <MoreVertical />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {billing.paid ? (
            // A ignorada sai pelo "Voltar a cobrar": reabrir a deixaria em
            // aberto cobrando zero, e o backend recusa.
            billing.waived ? null : (
              <DropdownMenuItem onSelect={() => setConfirming("reopen")}>
                <Undo2 /> Reabrir fatura
              </DropdownMenuItem>
            )
          ) : (
            <>
              <DropdownMenuItem onSelect={() => setConfirming("pay")} disabled={absorbed}>
                <Wallet /> Marcar como paga
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setConfirming("waive")}>
                <BadgePercent />
                {waivingDebtsOnly ? "Ignorar as dívidas acumuladas" : "Ignorar fatura"}
              </DropdownMenuItem>
            </>
          )}
          {billing.waived ? (
            <DropdownMenuItem onSelect={() => setConfirming("unwaive")}>
              <RotateCcw /> Voltar a cobrar
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void email()}>
            <Mail /> Reenviar por e-mail
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void recalc()}>
            <RefreshCw /> Recalcular o vendedor
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {confirming === "waive" && waivingDebtsOnly
              ? "Ignorar as dívidas acumuladas?"
              : confirming
                ? TITLES[confirming]
                : ""}
          </AlertDialogTitle>
          <AlertDialogDescription>{confirming ? description(confirming) : null}</AlertDialogDescription>

          <div className="space-y-1.5">
            <Label htmlFor="billing-reason">Motivo</Label>
            <Input
              id="billing-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={confirming ? PLACEHOLDERS[confirming] : ""}
            />
            <p className="text-caption text-muted-foreground">
              Fica gravado na fatura. É o que explica, meses depois, por que ela não passou pelo
              Mercado Pago.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirm()} disabled={saving}>
              {saving ? <Loader2 className="animate-spin" /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
